"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { 
  InputOTP, 
  InputOTPGroup, 
  InputOTPSlot 
} from "./ui/input-otp";
import { CheckCircle, AlertCircle, RefreshCw, ArrowRight } from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface OTPVerificationProps {
  onShowTerms?: () => void;
  onVerificationSuccess?: () => void;
}

export function OTPVerification({ onShowTerms, onVerificationSuccess }: OTPVerificationProps) {
  const [otp, setOtp] = React.useState("");
  const [resendAttempts, setResendAttempts] = React.useState(0);
  const [countdown, setCountdown] = React.useState(0);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [status, setStatus] = React.useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [autoProceedTimer, setAutoProceedTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [proceedCountdown, setProceedCountdown] = React.useState(3);

  const maxResendAttempts = 3;
  const cooldownTime = 30;

  // Countdown timer effect
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Auto-proceed after successful verification
  React.useEffect(() => {
    if (status === "success" && !showSuccess) {
      setShowSuccess(true);
      setProceedCountdown(3);
      const timer = setTimeout(() => {
        onVerificationSuccess?.();
      }, 3000);
      setAutoProceedTimer(timer);
      return () => clearTimeout(timer);
    }
  }, [status, showSuccess, onVerificationSuccess]);

  // Countdown timer for auto-proceed
  React.useEffect(() => {
    if (showSuccess && proceedCountdown > 0) {
      const timer = setTimeout(() => {
        setProceedCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, proceedCountdown]);

  const handleManualProceed = () => {
    if (autoProceedTimer) {
      clearTimeout(autoProceedTimer);
      setAutoProceedTimer(null);
    }
    onVerificationSuccess?.();
  };

  const handleOTPChange = (value: string) => {
    setOtp(value);
    setStatus("idle");
    setErrorMessage("");
    
    // Auto-verify when 6 digits are entered
    if (value.length === 6) {
      handleVerify(value);
    }
  };

  const handleVerify = async (otpValue: string = otp) => {
    setIsVerifying(true);
    setStatus("idle");
    
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock verification logic (in real app, this would be an API call)
      if (otpValue === "123456") {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage("Invalid OTP. Please try again.");
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendAttempts >= maxResendAttempts || countdown > 0) return;
    
    setResendAttempts(prev => prev + 1);
    setCountdown(cooldownTime);
    setOtp("");
    setStatus("idle");
    setErrorMessage("");
    
    // Simulate sending OTP
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  const canResend = resendAttempts < maxResendAttempts && countdown === 0;

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col items-center justify-center p-6 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-20 w-32 h-32 bg-purple-200/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-purple-100/10 to-transparent rounded-full" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative z-10 text-center"
        >
          <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-full mx-auto flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-gray-900 mb-4">
            Verification Successful! ✨
          </h1>
          
          <p className="text-gray-600 mb-8">
            Great! Now let's collect your documents to complete your onboarding.
          </p>

          <div className="space-y-4">
            <Button
              onClick={handleManualProceed}
              className="bg-purple-600 hover:bg-purple-700 text-white w-full"
            >
              Continue to Document Collection
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            <div className="flex items-center justify-center space-x-2 text-purple-600">
              <span className="text-sm">
                {proceedCountdown > 0 
                  ? `Auto-proceeding in ${proceedCountdown}s...` 
                  : "Proceeding now..."
                }
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-purple-100/10 to-transparent rounded-full" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 w-full max-w-md mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-8"
            >
              <div className="mx-auto mb-6 flex items-center justify-center">
                <img 
                  src={openTurfLogo} 
                  alt="OpenTurf Technologies" 
                  className="h-16 w-auto"
                />
              </div>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mb-4 text-gray-900"
            >
              Hi there! 👋 You're just a few steps away from joining the team.
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-gray-600"
            >
              Let's get started by verifying your identity.
            </motion.p>
          </div>

          {/* OTP Input Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20"
          >
            <div className="text-center mb-6">
              <p className="text-gray-700 mb-2">
                We've sent an OTP to your email.
              </p>
              <p className="text-sm text-gray-500">
                It's valid for 15 minutes.
              </p>
            </div>

            <div className="space-y-6">
              {/* OTP Input */}
              <div className="flex flex-col items-center space-y-4">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={handleOTPChange}
                  disabled={isVerifying || status === "success"}
                >
                  <InputOTPGroup>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <InputOTPSlot 
                        key={index} 
                        index={index}
                        className="w-12 h-12 text-lg border-purple-200 focus:border-purple-500 focus:ring-purple-500/20"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>

                {/* Status Messages */}
                <AnimatePresence mode="wait">
                  {isVerifying && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center space-x-2 text-purple-600"
                    >
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Verifying...</span>
                    </motion.div>
                  )}

                  {status === "success" && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="flex items-center space-x-2 text-green-600"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">Verification successful!</span>
                    </motion.div>
                  )}

                  {status === "error" && errorMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center space-x-2 text-red-600"
                    >
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">{errorMessage}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Resend OTP Section */}
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-3">
                  Didn't receive the code?
                </p>
                
                <Button
                  variant="ghost"
                  onClick={handleResendOTP}
                  disabled={!canResend}
                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 disabled:text-gray-400"
                >
                  {countdown > 0 ? (
                    `Resend in ${countdown}s`
                  ) : resendAttempts >= maxResendAttempts ? (
                    "Maximum attempts reached"
                  ) : (
                    "Resend OTP"
                  )}
                </Button>

                {resendAttempts > 0 && resendAttempts < maxResendAttempts && (
                  <p className="text-xs text-gray-500 mt-2">
                    {resendAttempts}/{maxResendAttempts} attempts used
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {/* Support message */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="text-center mt-8"
          >
            <p className="text-sm text-gray-500">
              Having trouble? Contact our support team for assistance.
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}